import { HealthComLocation } from '@chiroup/core/types/HealthComLocation.type';
import { QueryFunctionContext, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import locationService from '../services/location.service';

const getQuery = () => {
  return async (context: QueryFunctionContext) => {
    const path = context.queryKey[1] as string | null | undefined;
    const clinicId = (context.queryKey[2] as number | null | undefined) || null;

    // Path-based (legacy scheduling-site) first; the authed portal falls
    // back to the clinicId variant for clinics without a vanity URL.
    if (path) {
      return locationService.getLocation(path);
    }

    if (clinicId) {
      return locationService.getLocationByClinicId(clinicId);
    }

    return {};
  };
};

/**
 * Resolves the active clinic location for the current user.
 *
 * The authed patient portal no longer uses URL paths to identify a
 * clinic, so the caller (Container) passes in `overridePath` sourced
 * from the selected clinic state. We can't read that state via
 * `usePatientPortal()` here because `useLocation()` is called inside
 * the same component that provides the context — it would see the
 * default context, not the live state.
 *
 * Falls back to `useParams().path` for the legacy scheduling-site
 * entry point (pre-login, URL-based).
 */
// 5 minutes. Location data (address, phone, providers, disciplines)
// changes rarely — and certainly not within a single user session. The
// staleTime is load-bearing: without it react-query treats every new
// useLocation subscriber as needing a fresh fetch, so any component
// remount churn (Schedule unmounting/remounting because portalLoading
// flips, multiple consumers mounting in different render passes, etc.)
// turns into a stream of `/location/{path}` calls.
const LOCATION_STALE_TIME = 5 * 60 * 1000;

const useLocation = (overridePath?: string | null, clinicId?: number | null) => {
  const { path: urlPath } = useParams();
  const path = overridePath || urlPath;

  const { status, data, error, isFetching, refetch } = useQuery<HealthComLocation>(
    ['location', path, clinicId || null],
    getQuery(),
    {
      // Resolve by path, or by clinicId when the clinic has no vanity URL.
      enabled: !!(path || clinicId),
      staleTime: LOCATION_STALE_TIME,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
    }
  );

  return {
    status,
    data,
    error,
    isFetching,
    refetch,
    path,
  };
};

export default useLocation;
